import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useSubAccountCreationFormConfig from '../OrgChartContainer/CreateSubAccount/useSubAccountCreationFormConfig';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { Box, Card, BodyText, withStyles } from 'vopay';
import { Title, Button } from 'UI';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';
import styles from './SubAccountCreationPage.module.scss';

const cn = withStyles(styles);

export default function SubAccountCreationPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { createSubAccount } = useSubAccounts();

  const { renderFormElements, ctx } = useSubAccountCreationFormConfig({
    isReviewing: isUnderReview,
    onSubmit: async (values) => {
      try {
        setDataFetchingInfo(true);

        await createSubAccount(values);

        setDataFetchingInfo(false);

        const result = await alertDialog.success(
          t('SuccessDialog.title', { defaultValue: 'The Sub-Account Is Now Being Processed' }),
          t('SuccessDialog.description', {
            defaultValue: `The sub-account is being reviewed and if required, the sub-account will be sent an onboarding application (doesn't apply for certain types of sub-accounts). You can track the status of any sub-accounts through the sub-account listing.`,
          }),
          {
            confirmLabel: t('SuccessDialog.button', { defaultValue: 'View Sub-Accounts' }),
          },
        );

        if (result === 'confirm') {
          history.push('/sub-accounts-list');
        }
      } catch (err) {
        const msg =
          err instanceof Error && err.message
            ? err.message
            : t('Form.creationErrorMessage', { defaultValue: 'Sub-Account Creation Failed' });

        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
  });

  return (
    <Box jc="center">
      <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md" gapSize="32px">
        <Title>{t('title', { defaultValue: 'Create Sub-Account' })}</Title>

        <form onSubmit={ctx.handleSubmit}>
          <Card>
            <div className={cn('grid')}>{renderFormElements()}</div>
          </Card>

          {isUnderReview && (
            <BodyText className={cn('toa')}>
              <Trans
                i18nKey="SubAccounts.Create.Form.termsAgreement"
                ns="translations"
                components={[
                  <a
                    href="https://vopay.com/ca/legal/term-of-use/"
                    style={{ textDecoration: 'underline' }}
                    target="_blank"
                    rel="noreferrer"
                  />,
                ]}
              />
            </BodyText>
          )}

          <Box pt="md" jc="fe">
            {!isUnderReview && (
              <Button
                colorScheme="primary"
                onClick={() => {
                  setIsUnderReview(true);
                }}
                disabled={!ctx.isValid}
              >
                {tGlobal('Buttons.review', { defaultValue: 'Review' })}
              </Button>
            )}

            {isUnderReview && (
              <Box gapSize="10px">
                <Button
                  type="button"
                  onClick={() => {
                    setIsUnderReview(false);
                  }}
                >
                  {tGlobal('Buttons.edit', { defaultValue: 'Edit' })}
                </Button>

                <Button type="submit" disabled={!ctx.isValid} colorScheme="primary">
                  {tGlobal('Buttons.create', { defaultValue: 'Create' })}
                </Button>
              </Box>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  );
}
